import { useEffect, useState } from 'react';
import { Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import { generatePackageBlocks } from '@/lib/ai-package-blocks';
import { fetchContentBlocks, updatePackage } from '@/lib/api';
import type { ContentBlock, GiftPackage } from '@/lib/types';

interface Props {
  pkg: GiftPackage;
  /** Klant-uitgangspunten (van het project) die de toon sturen. */
  uitgangspunten?: string;
  /** Wordt aangeroepen nadat goed_doel_block_id op het pakket is gezet. */
  onPackageChanged?: () => void;
}

const fieldCls =
  'w-full rounded-md border border-bmade-200 bg-white text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400';

/**
 * PackageBlockGenerator — per-pakket offerteblokken.
 * Genereert "Voornaamste kenmerken" (bullets) en "Over het pakket" (alinea) via AI,
 * beide bewerkbaar. Daaronder een goed-doel-keuzelijst die het gekozen blok onder
 * de tekst toont en goed_doel_block_id op het pakket vastlegt.
 *
 * De gegenereerde tekst is lokaal-bewerkbaar; Mark plakt het resultaat zelf in de
 * presentatietekst. (Een directe koppeling naar client_presentation_text kan later.)
 */
export function PackageBlockGenerator({ pkg, uitgangspunten, onPackageChanged }: Props) {
  const [goedDoelBlocks, setGoedDoelBlocks] = useState<ContentBlock[]>([]);
  const [selectedId, setSelectedId] = useState<string>(pkg.goed_doel_block_id ?? '');
  const [kenmerken, setKenmerken] = useState<string[]>([]);
  const [overHetPakket, setOverHetPakket] = useState('');
  const [generating, setGenerating] = useState(false);
  const [savingDoel, setSavingDoel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasResult, setHasResult] = useState(false);

  useEffect(() => {
    fetchContentBlocks('goed_doel')
      .then(setGoedDoelBlocks)
      .catch(() => setGoedDoelBlocks([]));
  }, []);

  useEffect(() => {
    setSelectedId(pkg.goed_doel_block_id ?? '');
  }, [pkg.id, pkg.goed_doel_block_id]);

  const selectedBlock = goedDoelBlocks.find((b) => b.id === selectedId) ?? null;

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const result = await generatePackageBlocks({
        pkg,
        items: pkg.items ?? [],
        uitgangspunten: uitgangspunten || undefined,
        goedDoelTitle: selectedBlock?.title,
      });
      setKenmerken(result.kenmerken);
      setOverHetPakket(result.over_het_pakket);
      setHasResult(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pakket-tekst genereren mislukt');
    } finally {
      setGenerating(false);
    }
  }

  async function handleSelectDoel(id: string) {
    setSelectedId(id);
    setSavingDoel(true);
    setError(null);
    try {
      await updatePackage(pkg.id, { goed_doel_block_id: id || null });
      onPackageChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Goed doel opslaan mislukt');
    } finally {
      setSavingDoel(false);
    }
  }

  function updateKenmerk(index: number, value: string) {
    setKenmerken((prev) => prev.map((k, i) => (i === index ? value : k)));
  }

  function removeKenmerk(index: number) {
    setKenmerken((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3 rounded-lg border border-bmade-200 bg-bmade-50/60 p-3 dark:border-bmade-800 dark:bg-bmade-900/40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-bmade-900 dark:text-white">Pakket-tekst</h3>
          <p className="mt-0.5 text-[11px] text-bmade-500">
            Voornaamste kenmerken en een verhalend "Over het pakket". Bewerkbaar.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={generating}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
        >
          {generating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {hasResult ? 'Opnieuw genereren' : 'Genereer pakket-tekst'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {hasResult && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-bmade-700 dark:text-bmade-300">
                Voornaamste kenmerken
              </span>
              <button
                type="button"
                onClick={() => setKenmerken((prev) => [...prev, ''])}
                className="inline-flex items-center gap-1 text-[11px] text-accent-600 hover:text-accent-700 dark:text-accent-400"
              >
                <Plus className="h-3 w-3" />
                Regel
              </button>
            </div>
            {kenmerken.length === 0 ? (
              <p className="text-[11px] text-bmade-400">Geen kenmerken.</p>
            ) : (
              <div className="space-y-1.5">
                {kenmerken.map((k, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-bmade-400">•</span>
                    <input
                      type="text"
                      value={k}
                      onChange={(e) => updateKenmerk(i, e.target.value)}
                      className={`${fieldCls} px-2 py-1`}
                    />
                    <button
                      type="button"
                      onClick={() => removeKenmerk(i)}
                      className="shrink-0 rounded-md border border-bmade-200 p-1.5 text-bmade-500 transition-colors hover:border-red-400 hover:text-red-600 dark:border-bmade-700"
                      title="Regel verwijderen"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Over het pakket
            </span>
            <textarea
              value={overHetPakket}
              onChange={(e) => setOverHetPakket(e.target.value)}
              rows={6}
              className={`${fieldCls} resize-y px-3 py-2 leading-relaxed`}
              spellCheck
              lang="nl"
            />
          </label>
        </div>
      )}

      <div className="space-y-1.5 border-t border-bmade-200 pt-3 dark:border-bmade-800">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-bmade-700 dark:text-bmade-300">Goed doel</span>
          {savingDoel && <Loader2 className="h-3 w-3 animate-spin text-bmade-400" />}
        </div>
        <select
          value={selectedId}
          onChange={(e) => void handleSelectDoel(e.target.value)}
          className={`${fieldCls} px-2 py-1.5`}
        >
          <option value="">Geen goed doel</option>
          {goedDoelBlocks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.title}
            </option>
          ))}
        </select>
        {selectedBlock && (
          <div className="rounded-md border border-bmade-200 bg-white p-2.5 dark:border-bmade-800 dark:bg-bmade-950/40">
            <div className="text-xs font-medium text-bmade-900 dark:text-white">
              Over {selectedBlock.title}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-xs text-bmade-600 dark:text-bmade-400">
              {selectedBlock.body}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
